// store/app/thunks.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ApolloClient } from '@apollo/client';
import {
  GetAllInstrumentsQuery,
  GetAdminDataQuery,
  GetOrganizationInfoQuery,
} from '@/lib/gql/codegen-types';
import GET_ALL_INSTRUMENTS from '@/lib/gql/queries/GetAllInstruments.graphql';
import GET_ADMIN_DATA from '@/lib/gql/queries/GetAdminData.graphql';
import GET_ORGANIZATION_INFO from '@/lib/gql/queries/GetOrganizationInfo.graphql';

export const fetchInstruments = createAsyncThunk(
  'app/fetchInstruments',
  async (
    {
      client,
      force,
    }: {
      client: ApolloClient<unknown>;
      force?: boolean;
    },
    { rejectWithValue },
  ) => {
    if (!client) {
      return rejectWithValue('Apollo client is required for non-demo mode');
    }

    try {
      const pilotResult = await client.query<GetAllInstrumentsQuery>({
        query: GET_ALL_INSTRUMENTS,
        fetchPolicy: force ? 'network-only' : undefined,
      });

      if (pilotResult.error) {
        throw new Error(pilotResult.error?.message);
      }

      return pilotResult.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch facilities',
      );
    }
  },
);

export const fetchAdminData = createAsyncThunk(
  'app/fetchAdminData',
  async (
    { client, force }: { client: ApolloClient<unknown>; force?: boolean },
    { rejectWithValue },
  ) => {
    if (!client) {
      return rejectWithValue('Apollo client is required for non-demo mode');
    }

    try {
      const result = await client.query<GetAdminDataQuery>({
        query: GET_ADMIN_DATA,
        fetchPolicy: force ? 'network-only' : undefined,
      });

      return {
        data: result.data,
        error: undefined,
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch admin data',
      );
    }
  },
);

export const fetchOrganizationInfo = createAsyncThunk(
  'app/fetchOrganizationInfo',
  async (
    {
      client,
      organizationId,
      force,
    }: {
      client: ApolloClient<unknown>;
      organizationId: number;
      force?: boolean;
    },
    { rejectWithValue },
  ) => {
    if (!client) {
      return rejectWithValue('Apollo client is required for non-demo mode');
    }

    try {
      const result = await client.query<GetOrganizationInfoQuery>({
        query: GET_ORGANIZATION_INFO,
        variables: { organizationId },
        fetchPolicy: force ? 'network-only' : undefined,
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      return {
        data: result.data,
        organizationId,
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch organization info',
      );
    }
  },
);
